import React, { useEffect, useState } from 'react';
import { Sidebar } from './common/Catalyst/sidebar';
import {
  HomeIcon,
  ChartBarIcon,
  HomeModernIcon,
  Cog6ToothIcon,
  ArrowRightStartOnRectangleIcon,
  BellIcon,
  PaperAirplaneIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
  BuildingStorefrontIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlusIcon,
  MapIcon,
} from '@heroicons/react/16/solid';
import { ChevronDownIcon } from '@heroicons/react/16/solid';
import { LOGO_URL } from './shell/logo';
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from './common/Catalyst/dropdown';
import {
  clearAllTokens,
  clearRememberMe,
  clearSessionStorage,
  clearUserName,
  getUserTypeFromToken,
} from './utils/TokenUtils';
import { useUser } from './hooks/UserContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { authSessionService, companyService, hoaService } from './clients/ApiClients';
import { HoaReturn } from './clients/rust-api-client';
import {
  UserType,
  canCreateHoa,
  canCreateVendor,
  canCreateViolation,
  canCreateWorkOrder,
  canManageReservations,
  canManageUsers,
  canSendMemberMessage,
  canViewBankAccounts,
  canViewReservations,
  canViewRoadmap,
} from './constants/UserTypes';
import { usePendingReservationCount } from './reservation/usePendingReservationCount';
import { NavIconLink, NavItem, NavSection } from './shell/NavPrimitives';
import { SidebarFooter, SidebarUserRow } from './shell/SidebarFooter';
import { useCommandPalette } from './shell/CommandPaletteProvider';
import { useSyncedCompany, useSyncedHoas } from './sync/hooks';
import { useSyncHolding } from './sync/SyncProvider';
import { paletteShortcutLabel } from './shell/CommandPalette';

interface AppsidebarProps {
  currentPage: string;
}

/** How many associations the sidebar lists before "All HOAs". */
const HOA_LIMIT = 8;

/**
 * Linear-style sidebar (#603 step 3): workspace menu, jump-to and
 * quick-create at the top; dense primary items; collapsible sections,
 * with the associations this person works in as a favourites-style list.
 */
const AppSidebar: React.FC<AppsidebarProps> = ({ currentPage }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Name and email come from the profile fetch, not the token: the
  // management JWT carries no name or email at all.
  const {
    email: userEmail,
    fullName: userName,
    initials: userInitials,
    loading: profileLoading,
    companyId,
  } = useUser();
  const userType = getUserTypeFromToken();
  // Anyone who can view accounts (Owner/Admin/SuperAdmin) or manage connections
  // gets the Banking nav; sections inside the page gate themselves per-role.
  const canAccessBanking = canViewBankAccounts(userType);
  // Managers and BoardMembers manage nobody — the backend answers 403
  // to their list request — so they do not get the entry either.
  const canAccessUsers = canManageUsers(userType);
  const canNewViolation = canCreateViolation(userType);
  const canNewWorkOrder = canCreateWorkOrder(userType);
  const canMessage = canSendMemberMessage(userType);
  const canSeeReservations = canViewReservations(userType);
  const canNewReservation = canManageReservations(userType);
  const canNewHoa = canCreateHoa(userType);
  const canNewVendor = canCreateVendor(userType);
  const canSeeRoadmap = canViewRoadmap(userType);
  // The count is for whoever decides them; a board member sees the list.
  const pendingReservations = usePendingReservationCount(canNewReservation);

  const [fetchedCompanyName, setCompanyName] = useState<string | null>(null);
  // Every page mounts the sidebar, so this was a request per page (#561).
  const syncedCompany = useSyncedCompany(companyId);
  const haveSyncedCompany = !!syncedCompany;
  const companyName = syncedCompany?.name ?? fetchedCompanyName;
  const [fetchedHoas, setHoas] = useState<HoaReturn[]>([]);
  // From the sync engine's local copy when it has one (#561) — so an
  // association added or renamed anywhere appears here without a reload.
  const syncedHoas = useSyncedHoas();
  // Shown during the sync hold too (`SyncHold` in App), when the copy is on
  // its way: the two fetches below wait for it like every view does.
  const holding = useSyncHolding();
  const hoas = syncedHoas
    ? syncedHoas.filter((h) => h.is_active !== false)
    : fetchedHoas;
  const haveSynced = syncedHoas !== null;
  const {
    openPalette,
    openNewViolation,
    openNewWorkOrder,
    openSendMessage,
    openNewReservation,
    openNewHoa,
    openNewVendor,
  } = useCommandPalette();

  useEffect(() => {
    if (!companyId || haveSyncedCompany || holding) return;
    let cancelled = false;
    companyService
      .getCompany({ id: companyId })
      .then((r) => !cancelled && setCompanyName(r.data.name))
      .catch(() => {
        // Cosmetic: the header falls back to the product name.
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, haveSyncedCompany, holding]);

  useEffect(() => {
    if (haveSynced || holding) return;
    let cancelled = false;
    // Same source the HOA list uses: SuperAdmin sees every association,
    // everyone else their company's (the server narrows a board member
    // further).
    const request =
      userType === UserType.SUPER_ADMIN
        ? hoaService.getAllHoas()
        : companyId
          ? hoaService.getHoasByCompany({ companyId })
          : null;
    request
      ?.then((r) => {
        if (cancelled) return;
        setHoas(
          r.data
            .filter((h) => h.is_active !== false)
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      })
      .catch(() => {
        // The section just stays empty; the HOAs page reports errors.
      });
    return () => {
      cancelled = true;
    };
  }, [userType, companyId, haveSynced, holding]);

  const handleLogout = async () => {
    // Tell the server first. Discarding the token locally only makes this
    // browser forget it — the token itself stays valid until it expires,
    // so a copy taken from storage would still work. The server ends the
    // session for real.
    // Set when the session came from WorkOS. WorkOS keeps a session of
    // its own that only the browser can end, so ending ours is half the
    // job: without this the next sign-in goes straight through with no
    // password prompt, which is not what anyone means by "log out".
    let workosLogoutUrl: string | undefined;

    try {
      // Bounded so a hung or unreachable server cannot leave someone
      // stuck on a screen they asked to leave. Clearing locally below is
      // not conditional on this succeeding.
      const response = await authSessionService.logout({ timeout: 5000 });
      workosLogoutUrl = response.data?.workos_logout_url ?? undefined;
    } catch (err) {
      // Never trap someone in the app because sign-out failed. A token
      // that is already invalid returns 401 here, which is fine: the
      // outcome we wanted has happened either way.
      console.error('Server sign-out failed; clearing locally anyway', err);
    }
    clearAllTokens();
    clearUserName();
    clearRememberMe();
    clearSessionStorage();

    if (workosLogoutUrl) {
      // Leaves the app. WorkOS ends its session and sends the browser
      // back to /login itself, so there is no navigate() to race with —
      // and `replace` keeps the signed-in page out of history.
      window.location.replace(workosLogoutUrl);
      return;
    }

    navigate('/login');
  };

  const currentHoaId = /^\/hoa\/([^/]+)/.exec(location.pathname)?.[1];
  // Literal strings on purpose: Tailwind only generates classes it can
  // see in the source, so these must never be assembled at runtime.
  const iconButton =
    'rounded-md p-1.5 hover:bg-canvas-fg-strong/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-canvas-fg-strong/60';
  const iconClass = 'h-4 w-4 text-canvas-fg';

  return (
    <Sidebar className="bg-canvas">
      {/* Top row: workspace menu, jump to, new. */}
      <div className="flex items-center gap-1 px-3 pb-2 pt-3">
        <Dropdown>
          <DropdownButton
            plain
            className="!min-w-0 flex-1 !justify-start !px-1.5 !py-1 !text-canvas-fg-strong hover:!bg-canvas-fg-strong/10"
            aria-label="Workspace menu"
          >
            <img src={LOGO_URL} alt="" className="h-5 w-auto" />
            <span className="min-w-0 truncate text-[13px] font-semibold">
              {companyName ?? 'Epic HOAs'}
            </span>
            <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 !fill-canvas-fg" />
          </DropdownButton>
          <DropdownMenu anchor="bottom start" className="min-w-56">
            <DropdownItem href="/settings">
              <Cog6ToothIcon />
              <DropdownLabel>Settings</DropdownLabel>
            </DropdownItem>
            <DropdownDivider />
            {/* No `href` — signing out is an action. A link to a route that
                does not exist falls through to `*` and unmounts this while
                `handleLogout` is still awaiting the server. */}
            <DropdownItem onClick={handleLogout}>
              <ArrowRightStartOnRectangleIcon />
              <DropdownLabel>Sign out</DropdownLabel>
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>

        <button
          type="button"
          onClick={openPalette}
          aria-label="Search or jump to"
          aria-keyshortcuts="Meta+K Control+K"
          title={`Search (${paletteShortcutLabel()})`}
          className={iconButton}
        >
          <MagnifyingGlassIcon className={iconClass} aria-hidden />
        </button>
        {/* One "New…" menu for everything you can create here (#632),
            like Linear's single new-item button. Each starts from the page
            you are on. */}
        {(canNewViolation ||
          canNewWorkOrder ||
          canMessage ||
          canNewReservation ||
          canNewHoa ||
          canNewVendor) && (
          <Dropdown>
            <DropdownButton
              as="button"
              type="button"
              aria-label="New…"
              title="New…"
              className={iconButton}
            >
              <PlusIcon className={iconClass} aria-hidden />
            </DropdownButton>
            <DropdownMenu anchor="bottom end" className="min-w-48">
              {canNewViolation && (
                <DropdownItem onClick={() => openNewViolation()}>
                  <PencilSquareIcon />
                  <DropdownLabel>New violation</DropdownLabel>
                </DropdownItem>
              )}
              {canNewWorkOrder && (
                <DropdownItem onClick={() => openNewWorkOrder()}>
                  <WrenchScrewdriverIcon />
                  <DropdownLabel>New work order</DropdownLabel>
                </DropdownItem>
              )}
              {canMessage && (
                <DropdownItem onClick={() => openSendMessage()}>
                  <PaperAirplaneIcon />
                  <DropdownLabel>Send message</DropdownLabel>
                </DropdownItem>
              )}
              {canNewReservation && (
                <DropdownItem onClick={() => openNewReservation()}>
                  <CalendarDaysIcon />
                  <DropdownLabel>New reservation</DropdownLabel>
                </DropdownItem>
              )}
              {canNewVendor && (
                <DropdownItem onClick={() => openNewVendor()}>
                  <BuildingStorefrontIcon />
                  <DropdownLabel>New vendor</DropdownLabel>
                </DropdownItem>
              )}
              {canNewHoa && (
                <DropdownItem onClick={() => openNewHoa()}>
                  <HomeModernIcon />
                  <DropdownLabel>New association</DropdownLabel>
                </DropdownItem>
              )}
            </DropdownMenu>
          </Dropdown>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3 pb-3">
        {/* What is the manager's own: their home page and what is waiting
            for them. Everything else is an object list, under Workspace. */}
        <NavSection id="primary">
          <NavItem to="/home" icon={HomeIcon} current={currentPage === 'home'}>
            Home
          </NavItem>
          <NavItem to="/notifications" icon={BellIcon} current={currentPage === 'notifications'}>
            Notifications
          </NavItem>
          {/* An anchor, not a router link: /internal/ is the internal
              portal, a separate pod the ingress routes to on this host. */}
          {canSeeRoadmap && (
            <NavItem to="/internal/roadmap" icon={MapIcon} reload>
              Roadmap
            </NavItem>
          )}
        </NavSection>

        <NavSection id="workspace" title="Workspace">
          <NavItem to="/violations" icon={ChartBarIcon} current={currentPage === 'violations'}>
            Violations
          </NavItem>
          <NavItem to="/work-orders" icon={WrenchScrewdriverIcon} current={currentPage === 'work-orders'}>
            Work Orders
          </NavItem>
          {canSeeReservations && (
            <NavItem
              to="/reservations"
              icon={CalendarDaysIcon}
              current={currentPage === 'reservations'}
              trailing={
                pendingReservations > 0 ? (
                  <span
                    className="ml-auto rounded-full bg-canvas-fg-strong/15 px-1.5 text-[11px] font-semibold leading-4 text-canvas-fg-strong"
                    aria-label={`${pendingReservations} waiting for approval`}
                  >
                    {pendingReservations > 99 ? '99+' : pendingReservations}
                  </span>
                ) : undefined
              }
            >
              Reservations
            </NavItem>
          )}
          <NavItem to="/communications" icon={PaperAirplaneIcon} current={currentPage === 'communications'}>
            Communications
          </NavItem>
          {/* No "HOAs" item: "Your HOAs" below lists them. The full list
              stays at /hoas — its "All HOAs" link, an HOA's breadcrumb
              and ⌘K reach it. */}
          <NavItem to="/vendors" icon={BuildingStorefrontIcon} current={currentPage === 'vendors'}>
            Vendors
          </NavItem>
          {canAccessBanking && (
            <NavItem to="/banking" icon={BanknotesIcon} current={currentPage === 'banking'}>
              Banking
            </NavItem>
          )}
          {canAccessUsers && (
            <NavItem to="/users" icon={UsersIcon} current={currentPage === 'users'}>
              Users
            </NavItem>
          )}
        </NavSection>

        {hoas.length > 0 && (
          <NavSection id="hoas" title="Your HOAs">
            {hoas.slice(0, HOA_LIMIT).map((h) => (
              <NavItem
                key={h.id}
                to={`/hoa/${h.id}`}
                current={currentHoaId === h.id}
                icon={HomeModernIcon}
              >
                {h.name}
              </NavItem>
            ))}
            {hoas.length > HOA_LIMIT && (
              <NavItem to="/hoas">
                <span className="text-canvas-fg/70">
                  All {hoas.length} HOAs →
                </span>
              </NavItem>
            )}
          </NavSection>
        )}
      </div>

      {/* Who you are, and — the gear — your settings. Shown in the phone
          drawer too, now that it is the sidebar's way to Settings. */}
      <SidebarFooter
        user={
          <SidebarUserRow
            initials={userInitials}
            // Blank while the profile loads, rather than a placeholder
            // that changes as the first page opens.
            name={profileLoading ? null : userName || 'User'}
            email={profileLoading ? null : userEmail || 'No email provided'}
            trailing={
              <NavIconLink
                to="/settings"
                icon={Cog6ToothIcon}
                label="Settings"
                current={currentPage === 'settings'}
              />
            }
          />
        }
      />

    </Sidebar>
  );
};

export default AppSidebar;
